'use client';

import { Divider, IcArrowRight, Image, ImageCircleItem, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import type { ReverseProposalItem } from '@musinsa/services-curator';
import Link from 'next/link';
import { useEffect } from 'react';
import { pathsMusinsa } from '../../common';
import * as InfiniteList from '../../common/components/InfiniteList';
import { useReverseProposals } from '../hooks/useReverseProposals';
import {
  CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB,
  isMusinsaService,
  trackMyProposalCampaignClickMusinsa,
  trackMyProposalDetailClickMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';
import { EmptyState } from './common/EmptyState';

interface ReverseProposalListProps {
  status: 'WAITING' | 'APPROVED' | 'REJECTED';
  sort?: 'LATEST' | 'BRAND_NAME';
  onTotalCountChange?: (count: number) => void;
}

const EMPTY_TEXT: Record<string, { title: string; subTitle: string }> = {
  WAITING: {
    title: '제안한 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '+ 버튼을 눌러 브랜드에 협업을 제안해 보세요.',
  },
  APPROVED: {
    title: '수락된 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '제안한 협업이 수락되면 여기서 확인하실 수 있어요.',
  },
  REJECTED: {
    title: '거절된 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '제안한 협업이 거절되면 여기서 확인하실 수 있어요.',
  },
};

const STATUS_TEXT: Record<string, string> = {
  APPROVED: '협업 수락',
  REJECTED: '협업 거절',
  WAITING: '협업 수락 대기중',
};

export const ReverseProposalList = ({
  status,
  sort,
  onTotalCountChange,
}: ReverseProposalListProps) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isPlaceholderData } =
    useReverseProposals({ status, sort, size: 10 });

  const items = data?.pages.flatMap((page) => page.list) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  useEffect(() => {
    if (data && !isPlaceholderData) {
      onTotalCountChange?.(total);
    }
  }, [total, onTotalCountChange, data, isPlaceholderData]);

  if (items.length === 0 && !isFetching && !isPlaceholderData) {
    return (
      <EmptyState
        title={EMPTY_TEXT[status].title}
        subTitle={EMPTY_TEXT[status].subTitle}
        height={421}
      />
    );
  }

  return (
    <div className="relative flex flex-col h-full grow">
      <div className="box-border flex flex-col h-full px-4 overflow-y-auto scrollbar-hide grow">
        <InfiniteList.Root
          className="pt-3"
          loadMore={() => fetchNextPage()}
          isPending={isFetchingNextPage}
          hasMore={hasNextPage}
        >
          {items.map((item, index) => {
            const isLastItem = index === items.length - 1;
            return (
              <li key={item.id} className="list-none">
                <ReverseProposalItemCard item={item} status={status} isLast={isLastItem} />
                {!isLastItem && <Divider variant="line" className="my-3" />}
              </li>
            );
          })}
          <InfiniteList.Trigger />
        </InfiniteList.Root>
      </div>
    </div>
  );
};

function ReverseProposalItemCard({
  item,
  status,
  isLast,
}: {
  item: ReverseProposalItem;
  status: ReverseProposalListProps['status'];
  isLast: boolean;
}) {
  const { service } = useDeviceInfo();
  const statusText = STATUS_TEXT[item.status] ?? '';
  const isApproved = item.status === 'APPROVED';
  const statusColorScheme = isApproved ? 'blue' : 'gray-600';

  const titleText =
    item.status === 'WAITING' ? '할인 쿠폰 캠페인을 제안했어요.' : '할인 쿠폰 캠페인 제안';

  return (
    <div className={`flex flex-col gap-3 ${isLast ? 'pb-[72px]!' : 'pb-3'} pt-3`}>
      <Link
        href={pathsMusinsa.campaignSuggestionDetail({ query: { id: item.id } })}
        className="flex gap-2 justify-between"
        onClick={() => {
          if (!isMusinsaService(service)) return;
          trackMyProposalCampaignClickMusinsa({
            campaignTitle: titleText,
            appliedTab: CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB[status],
            suggestionNo: String(item.id),
            brandId: item.brandId,
          });
        }}
      >
        <div className="flex w-full min-w-0 gap-3">
          <div className="mt-[11px]! flex-shrink-0">
            <ImageCircleItem size="58" type="logo">
              <Image src={item.brandImageUrl} alt={item.brandName} />
            </ImageCircleItem>
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <Typography size="13" weight="semiBold" colorScheme={statusColorScheme}>
              {statusText}
            </Typography>
            <Typography size="13" weight="semiBold" colorScheme="black" className="truncate">
              {item.brandName}
            </Typography>
            <Typography size="13" weight="regular" colorScheme="black">
              {titleText}
            </Typography>
            <Typography size="13" weight="regular" colorScheme="black">
              {formatStartAt(item.startAt)}
            </Typography>
          </div>
        </div>
        <div className="w-4 shrink-0">
          <IcArrowRight strokeColor="gray-500" />
        </div>
      </Link>

      {isApproved && item.campaignProposalId && (
        <Link
          href={pathsMusinsa.campaignProposalDetail({ query: { id: item.campaignProposalId } })}
          className="flex justify-center items-center rounded-md px-3 py-[9px] border border-gray-300 cursor-pointer"
          onClick={() => {
            if (!isMusinsaService(service) || !item.campaignProposalId) return;
            trackMyProposalDetailClickMusinsa({
              campaignTitle: titleText,
              suggestionNo: String(item.campaignProposalId),
              brandId: item.brandId,
            });
          }}
        >
          <Typography size="13" weight="medium" colorScheme="black">
            브랜드의 캠페인 제안 보기
          </Typography>
        </Link>
      )}
    </div>
  );
}

/** startAt이 'YY.MM.DD' 형식일 때 'YYYY.MM.DD'로 변환 */
function formatStartAt(startAt: string): string {
  // API가 'yy.MM.dd' 형식으로 내려오는 경우 20xx 접두사 추가
  if (/^\d{2}\.\d{2}\.\d{2}$/.test(startAt)) {
    return `20${startAt}`;
  }
  return startAt;
}
